import { useLanguage } from '../providers/preferenceProvider';
import { useState, useEffect } from 'react';
import '../css/company.css';
import '../css/admin.css';
import Overlay from '../shared/overlays/editUser';
import { useConfig } from '../providers/configProvider';
import { getCompany, getCompaniesPlain, getCompanyUsers, updateCompanyImage, updateCompanyName, updateCompanyDesc } from '../api/company-api';
import { useToast } from '../providers/toastProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare, faPlus, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import ImageOverlay from '../shared/overlays/imageUpload';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import SearchableSelect from '../shared/overlays/searchableSelect';
import { ACL } from '../acls';

const Company = () => {
  const { t } = useLanguage();
  const { refresh, config, isAuthorized } = useConfig();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useToast();

  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayCompanyUser, setOverlayCompanyUser] = useState(null);

  const [showImageOverlay, setShowImageOverlay] = useState(false);
  const [createNew, setCreateNew] = useState(false);
  const [company, setCompany] = useState();

  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [descInput, setDescInput] = useState('');
  const [image, setImage] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  
  const [compUsers, setCompanyUsers] = useState({
    count: 0,
    users: []
  });

  const { id } = useParams();
  const companyId = id || config?.user?.companyId;

  const handleAddUser = () => {
    setCreateNew(true);
    setOverlayCompanyUser(null);
    setShowOverlay(true);
  }

  const handleEdit = (user) => {
    setCreateNew(false);
    setOverlayCompanyUser(user);
    setShowOverlay(true);
  };

  function openImageOverlay() {
    setShowImageOverlay(true);
  }

  function closeImageOverlay() {
    setShowImageOverlay(false);
  }
  
  function closeOverlay() {
    setShowOverlay(false);
  }

  const editName = () => {
    setNameInput(company.name);
    setIsEditingName(true);
    setIsEditingDesc(false);
  }

  const editDesc = () => {
    setDescInput(company.description);
    setIsEditingDesc(true);
    setIsEditingName(false);
  }

  const handleSaveName = async () => {
    showLoading();
    try {
      await updateCompanyName(companyId, nameInput);
      setIsEditingName(false);
      setCompany(prev => ({ ...prev, name: nameInput }));
    } catch (error) {
      console.error("Error updating company name:", error);
    }
    hideLoading();
  };

  const handleSaveDesc = async () => {
    showLoading();
    try {
      await updateCompanyDesc(companyId, descInput);
      setIsEditingDesc(false);
      setCompany(prev => ({ ...prev, description: descInput }));
    } catch (error) {
      console.error("Error updating company description:", error);
    }
    hideLoading();
  };

  async function handleSaveImage(file) {
    showLoading();
    try {
      await updateCompanyImage(companyId, file);
      closeImageOverlay();
      setImage(URL.createObjectURL(file));
    } catch (error) {
      console.error("Error updating company image:", error);
    }
    hideLoading();
  }

  async function handleUserUpdate() {
    showLoading();
    try {
      closeOverlay();
      fetchCompanyUsers();
      refresh();
    } catch (error) {
      console.error("Error updating company users:", error);
    }
    hideLoading();
  }

  const fetchCompanyUsers = async () => {
    try {
      const response = await getCompanyUsers(companyId);
      setCompanyUsers({
        count: response.data.length,
        users: response.data
      });
    } catch (error) {
    }
  };

  const fetchCompany = async () => {
    try {
      const response = await getCompany(companyId);
      setCompany(response.data);
      setImage(response.data.image);
    } catch (error) {
      console.error("Error fetching company:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await getCompaniesPlain();
      setAllCompanies(res.data);
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchCompanyUsers();
    fetchCompany();

    if (isAuthorized(ACL.COMPANY)) fetchCompanies();
  }, [config]);
  
  return (
    <>
      {company && (
        <main className="main-container">
          <div className='main-box'>
            <div className="py-4 px-4 flex row justify-between md:flex-row flex-col items-center">
              <div className='flex row'>
                <div className="relative inline-block">
                  <img src={image} alt={t.account} className="company-logo" />
                  {(config.user.companyManager || isAuthorized(ACL.COMPANY)) &&
                    <button onClick={openImageOverlay} className="edit-icon">
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                  }
                </div>          
                <div className="company-info ml-6 gap-2">
                  <div className="account-name">
                    {isEditingName ? (
                      <>
                        <input type="text" value={nameInput} onChange={(e) => setNameInput(e.target.value)} />
                        <button className="account-edit-button" onClick={handleSaveName}><FontAwesomeIcon icon={faCheck} /></button>
                        <button className="account-edit-button" onClick={() => setIsEditingName(false)}><FontAwesomeIcon icon={faXmark} /></button>
                      </>
                    ) : (
                      <>
                        {company.name}
                        {(config.user.companyManager || isAuthorized(ACL.COMPANY)) &&
                          <button className="edit-icon-inline" onClick={editName}>
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </button>
                        }
                      </>
                    )}
                  </div>
                  <div className="flex flex-row text-m">
                    {isEditingDesc ? (
                      <>
                        <textarea value={descInput} onChange={(e) => setDescInput(e.target.value)} className="p-1 border border-gray-300 rounded resize-x"/>
                        <button className="account-edit-button" onClick={handleSaveDesc}><FontAwesomeIcon icon={faCheck} /></button>
                        <button className="account-edit-button" onClick={() => setIsEditingDesc(false)}><FontAwesomeIcon icon={faXmark} /></button>
                      </>
                    ) : (
                      <>
                        {company.description}
                        {(config.user.companyManager || isAuthorized(ACL.COMPANY)) &&
                          <button className="edit-icon-inline" onClick={editDesc}>
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </button>
                        }
                      </>
                    )}
                  </div>
                </div>
              </div>
              {isAuthorized(ACL.COMPANY) && (
                <div className="relative self-start">
                  <button
                    className="admin-edit-button rounded"
                    onClick={() => setShowCompanyDropdown(prev => !prev)}
                  >
                    Change Company
                  </button>

                  {showCompanyDropdown && (
                    <SearchableSelect
                      label={t.select_company}
                      icon="company"
                      options={allCompanies}
                      value={company?.id}
                      onChange={(selectedId) => {
                        setShowCompanyDropdown(false);
                        navigate(`/company/${selectedId}`);
                        refresh();
                      }}
                      placeholder={t.select + " " + t.company}
                      name="company-select"
                      required={true}
                      error={false}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className='main-box mt-4'>
            <div className="user-list">
              <div className='main-header'>{t.users}</div>

              <div className="flex md:flex-row flex-col gap-2 justify-between items-center m-2 ml-4">
                <h1>{t.current_users + ": " + compUsers.count + "/" + company.maxUsers}</h1>
                {config.user.companyManager && (compUsers.count < company.maxUsers) &&  (
                  <button
                    className="admin-add-button"
                    disabled={!config.user.companyManager || (compUsers.count >= company.maxUsers)}
                    onClick={handleAddUser}
                  >
                    {t.add + " " + t.user} <FontAwesomeIcon icon={faPlus} />
                  </button>
                )}
              </div>

              <div className="admin-list-box">
                <div className="admin-list-header grid-cols-[3fr_2fr_1fr] px-4 py-2">
                  <div className="cursor-pointer select-none">
                    {t.user}
                  </div>
                  <div className="cursor-pointer select-none">
                    {t.email}
                  </div>
                  <div></div>
                </div>

                {compUsers.users && compUsers.users.map((user, index) => (
                  <div key={index} className="grid md:grid-cols-[3fr_2fr_1fr] items-center px-3 py-2 rounded-md mb-2 ml-2 bg-[var(--white-color)]">
                    <div className="flex items-center gap-4 ml-1 rounded-none">
                      <img src={user.image} alt={user.firstname} className="w-10 h-10 rounded-md object-covers" />
                      <span className="font-medium">{user.firstname} {user.lastname}</span>
                      {user.companyManager && <div className="manager-badge">{t.company_manager}</div>}
                    </div>
                    <div className="text-sm justify-self-start">{user.email}</div>

                    <div className="text-right">
                      {(config.user.companyManager || isAuthorized(ACL.COMPANY)) && (
                        <button  
                          className="admin-edit-button"
                          disabled={(!config.user.companyManager && !isAuthorized(ACL.COMPANY))} 
                          onClick={() => handleEdit(user)}
                        > 
                          {t.edit} <FontAwesomeIcon icon={faPenToSquare} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <div className="grid grid-cols-4 px-4 py-2">
                  <div><h2>{t.showing.replace("{ph_length}", compUsers.count).replace("{ph_count}", compUsers.count)}</h2></div>
                </div>

              </div>
            </div>
          </div>
        </main>
      )}; 
      {showOverlay && company && (
        <Overlay fixedCompany={company} isOpen={showOverlay} onClose={closeOverlay} user={overlayCompanyUser} onRefresh={handleUserUpdate} createNew={createNew} admin={false} />
      )}

      {showImageOverlay && (
        <ImageOverlay isOpen={showImageOverlay} onClose={closeImageOverlay} image={company.image} handleSaveImage={handleSaveImage} />
      )}
    </>
  );
};

export default Company;
